/* ==========================================================================
   Fully Loaded Offer
   --------------------------------------------------------------------------
   Drives the inline free-accessory checklist in the product buy column:

     - moves itself above the quantity selector / Add to Cart button
     - keeps locked free rows checked (they are a statement, not a choice)
     - recalculates the savings box on add-on toggle, quantity and variant change
     - takes over Add to Cart and posts the bike plus every ticked accessory to
       /cart/add.js as separate line items

   Free accessories go in at their normal variant price and are zeroed by the
   "Fully Loaded" automatic discount, so cart and checkout show the discount
   natively. Nothing here hardcodes a $0 price.
   ========================================================================== */

(function () {
  "use strict";

  if (window.customElements && window.customElements.get("fully-loaded-offer")) {
    return;
  }

  var DEFAULT_FORMAT = "${{amount}}";

  /* --- Money -------------------------------------------------------------- */

  function withDelimiters(cents, precision, thousands, decimal) {
    var number = (cents / 100).toFixed(precision);
    var parts = number.split(".");
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    return dollars + (parts[1] ? decimal + parts[1] : "");
  }

  function formatMoney(cents, format) {
    var formatString = format || DEFAULT_FORMAT;
    var match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      return formatString;
    }

    var value;
    switch (match[1]) {
      case "amount_no_decimals":
        value = withDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = withDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_space_separator":
        value = withDelimiters(cents, 2, " ", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = withDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = withDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = withDelimiters(cents, 0, " ", ",");
        break;
      default:
        value = withDelimiters(cents, 2, ",", ".");
    }

    return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* Matches Liquid's money_without_trailing_zeros: drop ".00" / ",00". */
  function trimZeros(money) {
    return money.replace(/([.,])00(\D*)$/, "$2");
  }

  function fill(template, values) {
    return Object.keys(values).reduce(function (out, key) {
      return out.split("[[" + key + "]]").join(values[key]);
    }, template || "");
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  /* --- Element ------------------------------------------------------------ */

  class FullyLoadedOfferElement extends HTMLElement {
    connectedCallback() {
      /* place() re-inserts this node, which fires connectedCallback again. */
      if (this._booted) {
        return;
      }
      this._booted = true;

      var configEl = this.querySelector("[data-fl-config]");
      try {
        this.config = configEl ? JSON.parse(configEl.textContent) : {};
      } catch (error) {
        this.config = {};
      }

      this.moneyFormat =
        this.config.moneyFormat ||
        (window.theme && window.theme.moneyFormat) ||
        DEFAULT_FORMAT;

      this.rows = Array.prototype.slice.call(this.querySelectorAll("[data-fl-row]"));
      this.outputs = {};
      this.templates = {};
      Array.prototype.forEach.call(this.querySelectorAll("[data-fl-out]"), (el) => {
        var key = el.getAttribute("data-fl-out");
        this.outputs[key] = el;
        if (el.hasAttribute("data-fl-template")) {
          this.templates[key] = el.getAttribute("data-fl-template");
        }
      });

      this.form = this.resolveForm();
      this.place();
      this.arrange();
      this.bind();
      this.recalculate();
      this.watchForLateWidgets();
    }

    resolveForm() {
      return (
        this.closest('form[action*="/cart/add"]') ||
        document.querySelector('form.product-form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    /* Move above the quantity selector / Add to Cart button, below the price.
       That target lives inside the buy-buttons block, which <product-rerender>
       replaces on variant change — onRerender puts us back. */
    place() {
      if (this.dataset.flPlace !== "buy-column") {
        return;
      }

      var scope = this.form || document;
      var anchor =
        scope.querySelector(".product-button_wrapper") ||
        document.querySelector(".product-button_wrapper") ||
        document.getElementById("product_purchase-btn") ||
        document.querySelector(".product_purchase__wrapper");

      if (!anchor || !anchor.parentNode || anchor.previousElementSibling === this) {
        return;
      }

      anchor.parentNode.insertBefore(this, anchor);
      this.form = this.resolveForm();
    }

    /* Put the buy column in the order the offer needs:
         ... Colour, Frame Size, THIS STACK, Purchase Options, Add to Cart ...
       Affirm is lifted out of the duplicate price block (which CSS then hides)
       and Advanced Options is pushed below the stack behind a toggle, so nothing
       sits between the variant selectors and the offer. */
    arrange() {
      this.moveAffirm();
      this.handleDeposit();
      this.moveAdvancedOptions();
    }

    /* The duplicate price block is hidden by CSS, so Affirm has to come out of
       it first or the "starting at $/mo" line disappears with it. */
    moveAffirm() {
      var slot = document.querySelector("[data-fl-finance-slot]");
      var affirm = document.querySelector(".affirm-as-low-as");
      if (affirm) {
        this._affirmNode = affirm;
      }
      if (slot && affirm && !slot.contains(affirm)) {
        slot.appendChild(affirm);
      }
    }

    /* The Affirm app captures its insertion anchor once, at init. On a variant
       change it removes its line and re-inserts against that anchor — which
       <product-rerender> has already replaced, so the node is detached and the
       line never comes back. (Reproducible on the published theme with none of
       this section's code present.) Put our own reference back and let the SDK
       re-render it against the new price. */
    restoreAffirm() {
      if (document.querySelector(".affirm-as-low-as")) {
        return;
      }
      var slot = document.querySelector("[data-fl-finance-slot]");
      if (!slot || !this._affirmNode) {
        return;
      }

      var bike = this.bike();
      if (bike && bike.price) {
        this._affirmNode.dataset.amount = String(bike.price);
      }
      slot.appendChild(this._affirmNode);

      try {
        if (window.affirm && window.affirm.ui && window.affirm.ui.refresh) {
          window.affirm.ui.refresh();
        }
      } catch (error) {
        /* Affirm blocked or not loaded: nothing to refresh. */
      }
    }

    handleDeposit() {
      var deposit = document.getElementById("downpay-block");
      if (!deposit) {
        return;
      }

      if (this.config.hidePurchaseOptions === false) {
        /* Shown: #downpay-block renders outside the product form, so CSS ordering
           cannot interleave it. It belongs between the stack and Add to Cart. */
        var anchor = this.buyAnchor();
        if (!anchor || !anchor.parentNode) {
          return;
        }
        if (deposit.previousElementSibling !== this || deposit.nextElementSibling !== anchor) {
          anchor.parentNode.insertBefore(deposit, anchor);
        }
        return;
      }

      /* Hidden: the deposit radio defaults to ON and its selling_plan input lives
         inside the product form, so hiding the widget alone would apply a CA$500
         deposit the shopper can neither see nor change. Switch downpay to pay in
         full first, which makes it clear the input itself. */
      var full = deposit.querySelector('input[value="purchase_option_pay_in_full"]');
      if (full && !full.checked) {
        full.checked = true;
        full.click();
        full.dispatchEvent(new Event("change", { bubbles: true }));
      }

      deposit.setAttribute("data-fl-suppressed", "true");
      var info = document.querySelector(".downpay-customer-info");
      if (info) {
        info.setAttribute("data-fl-suppressed", "true");
      }
    }

    moveAdvancedOptions() {
      var advanced = document.querySelector(".fl-advanced");
      if (!advanced || advanced.dataset.flMoved === "true") {
        return;
      }

      var anchor = this.buyAnchor();
      if (!anchor || !anchor.parentNode) {
        return;
      }

      advanced.dataset.flMoved = "true";
      advanced.dataset.flCollapsed = "true";
      advanced.id = advanced.id || "fl-advanced-options";

      var toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "fl-advanced-toggle";
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-controls", advanced.id);
      toggle.innerHTML =
        "Show advanced options" +
        '<span class="fl-advanced-toggle__hint">Frame bundles, pannier bags and pedal colours.</span>';

      toggle.addEventListener("click", function () {
        var collapsed = advanced.dataset.flCollapsed === "true";
        advanced.dataset.flCollapsed = collapsed ? "false" : "true";
        toggle.setAttribute("aria-expanded", collapsed ? "true" : "false");
        toggle.firstChild.nodeValue = collapsed ? "Hide advanced options" : "Show advanced options";
      });

      /* Below Add to Cart, well clear of the variant selectors. */
      var after = anchor.nextElementSibling;
      anchor.parentNode.insertBefore(toggle, after);
      anchor.parentNode.insertBefore(advanced, toggle.nextElementSibling);
    }

    buyAnchor() {
      var scope = this.form || document;
      return (
        scope.querySelector(".product-button_wrapper") ||
        document.querySelector(".product-button_wrapper")
      );
    }

    /* downpay injects its UI after we boot, so watch for it rather than racing. */
    watchForLateWidgets() {
      var target = document.querySelector(".product-form__wrapper") || document.body;
      var observer = new MutationObserver(() => {
        this.moveAffirm();
        this.restoreAffirm();
        this.handleDeposit();
      });
      /* No timeout: Affirm's app removes and re-inserts its line on every
         variant change, and can arrive well after boot on a slow connection.
         A disconnect would strand it in the hidden duplicate price block.
         Both handlers are idempotent, so observing for the life of the page is
         cheap. */
      observer.observe(target, { childList: true, subtree: true });
    }

    bind() {
      this.addEventListener("click", this.onRowClick.bind(this), true);
      this.addEventListener("change", this.onRowChange.bind(this));

      this._onQuantity = this.onQuantity.bind(this);
      document.addEventListener("click", this._onQuantity);
      document.addEventListener("change", this._onQuantity);
      document.addEventListener("keyup", this._onQuantity);
      document.addEventListener("focusout", this._onQuantity);

      document.addEventListener("product:rerendered", this.onRerender.bind(this));

      if (this.form) {
        this._onAddToCart = this.onAddToCart.bind(this);
        this.form.addEventListener("click", this._onAddToCart, true);
      }
    }

    /* --- Locking ---------------------------------------------------------- */

    onRowClick(event) {
      var row = event.target.closest ? event.target.closest("[data-fl-row]") : null;
      if (!row || row.dataset.flLocked !== "true") {
        return;
      }
      /* Locked rows do nothing at all, however they are clicked. */
      event.preventDefault();
    }

    onRowChange(event) {
      var row = event.target.closest ? event.target.closest("[data-fl-row]") : null;
      if (row && row.dataset.flLocked === "true") {
        var input = row.querySelector("[data-fl-input]");
        if (input) {
          input.checked = true;
        }
      }
      this.recalculate();
    }

    /* --- Quantity / variant ---------------------------------------------- */

    onQuantity(event) {
      if (!this.form || !this.form.contains(event.target)) {
        return;
      }

      var isQuantity =
        event.target.matches('[name="quantity"]') ||
        !!(event.target.closest &&
          event.target.closest(
            '[data-action="increase-picker-quantity"],[data-action="decrease-picker-quantity"]'
          ));

      var isVariant =
        event.target.matches('[name="id"]') ||
        !!(event.target.closest && event.target.closest("[data-block-type='variant-selector']"));

      if (!isQuantity && !isVariant) {
        return;
      }

      /* The theme's quantity buttons set input.value in their own bubble-phase
         handler, so read it on the next tick. */
      window.setTimeout(this.recalculate.bind(this), 0);
    }

    onRerender() {
      if (!document.contains(this)) {
        this.place();
      }

      var form = this.resolveForm();
      if (form !== this.form) {
        if (this.form && this._onAddToCart) {
          this.form.removeEventListener("click", this._onAddToCart, true);
        }
        this.form = form;
        if (this.form && this._onAddToCart) {
          this.form.addEventListener("click", this._onAddToCart, true);
        }
      }

      window.setTimeout(() => {
        this.moveAffirm();
        this.restoreAffirm();
        this.recalculate();
      }, 0);
    }

    /* --- Reading state --------------------------------------------------- */

    quantity() {
      var input = this.form && this.form.querySelector('[name="quantity"]');
      var qty = input ? toInt(input.value) : 1;
      return qty > 0 ? qty : 1;
    }

    bike() {
      var variants = this.config.variants || {};
      var input = this.form && this.form.querySelector('[name="id"]');
      var id = input && input.value ? String(input.value) : String(this.config.bikeVariantId || "");
      var variant = variants[id] || variants[String(this.config.bikeVariantId)] || null;

      return {
        id: id,
        price: variant ? variant.price : 0,
        compare: variant ? variant.compare || variant.price : 0
      };
    }

    selection() {
      var free = [];
      var paid = [];

      this.rows.forEach(function (row) {
        var input = row.querySelector("[data-fl-input]");
        if (!input || !input.checked) {
          return;
        }

        var item = {
          row: row,
          variantId: toInt(row.dataset.flVariant),
          compare: toInt(row.dataset.flCompare),
          price: toInt(row.dataset.flPrice)
        };

        if (row.dataset.flFree === "true") {
          free.push(item);
        } else {
          paid.push(item);
        }
      });

      return { free: free, paid: paid };
    }

    /* --- Totals ---------------------------------------------------------- */

    recalculate() {
      var qty = this.quantity();
      var bike = this.bike();
      var picked = this.selection();

      var freeValue = picked.free.reduce(function (sum, item) {
        return sum + item.compare;
      }, 0);

      var addons = picked.paid.reduce(function (sum, item) {
        return sum + item.price;
      }, 0);

      var upgrades = freeValue * qty;
      var addonsTotal = addons * qty;
      var pay = bike.price * qty + addonsTotal;
      var regular = bike.compare * qty + upgrades + addonsTotal;
      var save = regular - pay;
      var percent = regular > 0 ? Math.round((save / regular) * 100) : 0;

      var money = (cents) => trimZeros(formatMoney(cents, this.moneyFormat));
      var count = String(picked.free.length);

      this.write("summary", fill(this.template("summary"), {
        count: count,
        value: money(upgrades),
        price: money(0)
      }));

      this.write("upgradesLabel", fill(this.template("upgradesLabel"), { count: count }));
      this.write("bikeCompare", money(bike.compare * qty));
      this.write("upgradesValue", money(upgrades));
      this.write("addonsValue", money(addonsTotal));
      this.write("payTotal", money(pay));

      this.write("saveBar", fill(this.template("saveBar"), {
        amount: money(save),
        percent: String(percent)
      }));

      var addonsRow = this.outputs.addonsRow;
      if (addonsRow) {
        addonsRow.hidden = addonsTotal === 0;
      }

      /* No live sale means no struck bike price: a strikethrough equal to the
         pay-today number reads as a broken discount. */
      var bikeRow = this.outputs.bikeRow;
      if (bikeRow) {
        bikeRow.hidden = bike.compare <= bike.price;
      }

      /* The "+ X in free gear" line in the header block belongs to this stack,
         so it is filled from here and stays hidden until it has a real number. */
      var gear = document.querySelector('[data-fl-out="freeGear"]');
      if (gear) {
        var template = gear.getAttribute("data-fl-template") || "+ [[value]] in free gear";
        var text = fill(template, { value: money(upgrades), count: count });
        if (gear.textContent.trim() !== text) {
          gear.textContent = text;
        }
        gear.hidden = upgrades === 0;
      }

      /* Rows that stand in for a theme widget keep that widget's line item
         property in sync, e.g. Theftcare. */
      this.syncRowProperties();

      this.dispatchEvent(
        new CustomEvent("fully-loaded:change", {
          bubbles: true,
          detail: { quantity: qty, pay: pay, regular: regular, save: save, percent: percent }
        })
      );
    }

    /* Mirror a ticked row's property into the hidden input the theme left behind,
       so anything else reading the form sees the same answer we send. */
    syncRowProperties() {
      this.rows.forEach(function (row) {
        var name = row.dataset.flPropName;
        if (!name) {
          return;
        }
        var input = document.querySelector('[data-fl-property-slot="' + name + '"]');
        if (!input) {
          return;
        }
        var checked = row.querySelector("[data-fl-input]");
        input.value = checked && checked.checked ? row.dataset.flPropValue || "" : "";
      });
    }

    template(key) {
      return this.templates[key] || "";
    }

    /* Only touch the DOM on a real change, so aria-live does not announce the
       server-rendered figures again on load. */
    write(key, text) {
      var el = this.outputs[key];
      if (el && el.textContent.trim() !== text) {
        el.textContent = text;
      }
    }

    /* --- Add to Cart ------------------------------------------------------ */

    onAddToCart(event) {
      var button = event.target.closest && event.target.closest('[data-action="add-to-cart"]');
      if (!button || !this.form || !this.form.contains(button)) {
        return;
      }

      var items = this.lineItems();
      /* Nothing of ours to add: let the theme handle it untouched. */
      if (!items || items.length < 2) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      this.clearError();
      button.setAttribute("disabled", "disabled");
      document.dispatchEvent(new CustomEvent("theme:loading:start"));

      var addUrl = ((window.routes && window.routes.cartAddUrl) || "/cart/add") + ".js";

      fetch(addUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items: items })
      })
        .then((response) => {
          document.dispatchEvent(new CustomEvent("theme:loading:end"));

          if (!response.ok) {
            return response.json().then((body) => {
              throw new Error((body && body.description) || "");
            });
          }

          this.dispatchEvent(
            new CustomEvent("product:added", {
              bubbles: true,
              detail: { items: items, quantity: this.quantity() }
            })
          );

          if (this.config.cartRedirect === false) {
            document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
            button.removeAttribute("disabled");
            return;
          }

          window.location.replace(
            ((window.routes && window.routes.cartUrl) || "/cart") + "/?_t=" + Date.now()
          );
        })
        .catch((error) => {
          document.dispatchEvent(new CustomEvent("theme:loading:end"));
          button.removeAttribute("disabled");
          this.showError(error && error.message ? error.message : this.config.errorText);
        });
    }

    /* Bike line item plus one line item per ticked accessory. Free accessories
       carry the free-line property so the automatic discount and reporting can
       key off it. */
    lineItems() {
      if (!this.form) {
        return null;
      }

      var idInput = this.form.querySelector('[name="id"]');
      var bikeId = toInt(idInput && idInput.value) || toInt(this.config.bikeVariantId);
      if (!bikeId) {
        return null;
      }

      var qty = this.quantity();
      var key = 1000000000 + Math.floor(Math.random() * 9000000000);
      var picked = this.selection();
      var freeProperty = this.config.freeProperty || "_fully_loaded";

      var accessories = [];
      var ids = [];

      /* Options the theme's own upgrade UI has selected, so intercepting Add to
         Cart does not drop them. Mirrors ProductAdditionalOptions. */
      Array.prototype.forEach.call(
        this.form.querySelectorAll(".additional-option-item__wrapper"),
        function (wrapper) {
          var id = 0;
          var select = wrapper.querySelector("select");
          var checked = wrapper.querySelector("input[data-id]:checked");

          if (select && select.options[select.selectedIndex]) {
            id = toInt(select.options[select.selectedIndex].getAttribute("data-id"));
          }
          if (!id && checked) {
            id = toInt(checked.getAttribute("data-id"));
          }
          if (id > 1) {
            ids.push(id);
            accessories.push({
              id: id,
              quantity: qty,
              properties: { _ParentID: String(bikeId), _key: key }
            });
          }
        }
      );

      picked.free.concat(picked.paid).forEach(function (item) {
        if (!item.variantId) {
          return;
        }

        var properties = { _ParentID: String(bikeId), _key: key };
        if (item.row.dataset.flFree === "true") {
          properties[freeProperty] = "true";
        }

        ids.push(item.variantId);
        accessories.push({ id: item.variantId, quantity: qty, properties: properties });
      });

      var bikeProperties = this.formProperties();

      picked.free.concat(picked.paid).forEach(function (item) {
        var name = item.row.dataset.flPropName;
        if (name) {
          bikeProperties[name] = item.row.dataset.flPropValue || "";
        }
      });

      bikeProperties._key = key;
      bikeProperties._IDLine = ids.join(",");

      var bike = { id: bikeId, quantity: qty, properties: bikeProperties };

      /* With Purchase Options hidden the order is pay in full, whatever a
         leftover downpay input still holds. */
      if (this.config.hidePurchaseOptions === false) {
        var sellingPlan = this.form.querySelector('[name="selling_plan"]');
        if (sellingPlan && sellingPlan.value) {
          bike.selling_plan = sellingPlan.value;
        }
      }

      return accessories.concat([bike]);
    }

    /* Every properties[...] field the theme would have submitted. */
    formProperties() {
      var properties = {};

      Array.prototype.forEach.call(
        this.form.querySelectorAll("[name^='properties[']"),
        function (field) {
          var match = field.getAttribute("name").match(/^properties\[(.+)\]$/);
          if (!match || field.disabled) {
            return;
          }
          if ((field.type === "checkbox" || field.type === "radio") && !field.checked) {
            return;
          }
          properties[match[1]] = field.value;
        }
      );

      return properties;
    }

    /* --- Errors ----------------------------------------------------------- */

    showError(message) {
      this.clearError();
      var el = document.createElement("p");
      el.className = "fl-offer__error";
      el.setAttribute("role", "alert");
      el.textContent = message || "Something went wrong. Please try again.";
      this.appendChild(el);
    }

    clearError() {
      var existing = this.querySelector(".fl-offer__error");
      if (existing) {
        existing.remove();
      }
    }
  }

  window.customElements.define("fully-loaded-offer", FullyLoadedOfferElement);
})();
